import { useState, useCallback, useEffect } from "react";
import { requestService, GetPatientRequestsResponseDto } from "src/api/request/requestService";
import type {
  RequestListTabValue,
  CompletedRequestRow,
  RequestDetailRow,
  AllRequestRow,
  AllVisitsRequestRow,
} from "../types";

// Beklemede durumu için psId
const PENDING_STATUS_ID = 100;

interface UseRequestListDataParams {
  ptId: string | number | undefined;
  upn?: string | number;
  open: boolean;
}

interface UseRequestListDataReturn {
  activeTab: RequestListTabValue;
  setActiveTab: (value: RequestListTabValue) => void;
  completedRequests: CompletedRequestRow[];
  requestDetails: RequestDetailRow[];
  pendingRequestDetails: RequestDetailRow[];
  outOfRequestServices: RequestDetailRow[];
  allRequests: AllRequestRow[];
  allVisitsRequests: AllVisitsRequestRow[];
  selectedRequestId: number | string | null;
  setSelectedRequestId: (id: number | string | null) => void;
  loading: boolean;
  refetch: () => Promise<void>;
  fetchRequestDetails: (requestId: number) => Promise<void>;
  fetchAllVisitsRequests: (days: number) => Promise<void>;
  hasCancelPrivilege: boolean;
  detailsLoading: boolean;
}

function mapToCompletedRequestRow(dto: GetPatientRequestsResponseDto): CompletedRequestRow {
  return {
    id: dto.id,
    performedBy: dto.cuser ?? "",
    cuser: dto.cuser ?? "",
    actionDate: dto.process_ctime ?? dto.ptime ?? "",
    requestType: dto.object_name ?? "",
    departmentName: dto.dept_name ?? "",
    doctorName: dto.dr_name ?? "",
    status: dto.status_name ?? "",
    requestDate: dto.ctime ?? "",
  };
}

function mapToRequestDetailRow(dto: any, isOutside: boolean): RequestDetailRow {
  return {
    id: dto.id ?? dto.prd_id,
    actionName: dto.name ?? dto.process_name ?? "",
    note: dto.note ?? "",
    quantity: dto.quantity ?? 1,
    requestStatus: dto.status_name ?? dto.request_status ?? "",
    actionStatus: isOutside ? (dto.status ?? dto.status_name ?? "") : "",
    pacsStatus: dto.ps2_name ?? dto.pacs_status ?? "",
    consultationNote: dto.cr_note ?? dto.consultation_note ?? "",
    actionDate: dto.process_ctime ?? dto.ptime ?? dto.action_date ?? "",
    performedBy: dto.pps_cuser ?? dto.cuser ?? "",
    requestDate: dto.ctime ?? dto.request_date ?? "",
  };
}

function mapToAllRequestRow(dto: any): AllRequestRow {
  return {
    id: dto.prd_id ?? dto.id,
    requestId: dto.id,
    actionName: dto.process_name ?? dto.name ?? dto.action_name ?? "",
    actionDate: dto.process_ctime ?? dto.request_ctime ?? dto.ctime ?? dto.action_date ?? "",
    note: dto.note ?? "",
    quantity: dto.quantity ?? 1,
    pacsStatus: dto.pacs_status_name ?? dto.ps2_name ?? dto.pacs_status ?? "",
    consultationNote: dto.cr_note ?? dto.consultation_note ?? "",
    doctorName: dto.dr_name ?? "",
    departmentName: dto.dept_name ?? "",
    requestStatus: dto.request_status_name ?? dto.status_name ?? dto.request_status ?? "",
    requestDate: dto.request_ctime ?? dto.ctime ?? dto.request_date ?? "",
    serviceType: dto.service_type ?? dto.object_name ?? "",
    prdId: dto.prd_id ?? dto.pro_id ?? dto.process_id,
    performedBy: dto.cuser ?? dto.performedBy ?? "",
  };
}

export function useRequestListData({
  ptId,
  upn,
  open,
}: UseRequestListDataParams): UseRequestListDataReturn {
  const [activeTab, setActiveTab] = useState<RequestListTabValue>("completed");
  const [completedRequests, setCompletedRequests] = useState<CompletedRequestRow[]>([]);
  const [requestDetails, setRequestDetails] = useState<RequestDetailRow[]>([]);
  const [pendingRequestDetails, setPendingRequestDetails] = useState<RequestDetailRow[]>([]);
  const [outOfRequestServices, setOutOfRequestServices] = useState<RequestDetailRow[]>([]);
  const [allRequests, setAllRequests] = useState<AllRequestRow[]>([]);
  const [allVisitsRequests, setAllVisitsRequests] = useState<AllVisitsRequestRow[]>([]);
  const [selectedRequestId, setSelectedRequestId] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [hasCancelPrivilege, setHasCancelPrivilege] = useState(false);

  // Handle tab change with reset
  const handleTabChange = useCallback((newValue: RequestListTabValue) => {
    setActiveTab(newValue);
    setSelectedRequestId(null);
    setRequestDetails([]);
    setDetailsLoading(false);
  }, []);

  const fetchRequestDetails = useCallback(async (requestId: number) => {
    setDetailsLoading(true);
    try {
      const detailsResp = await requestService.getPatientRequestDetails(requestId);
      if (detailsResp?.issuccess && detailsResp?.data) {
        const mapped = detailsResp.data.map((dto) => mapToRequestDetailRow(dto, false));
        setRequestDetails(mapped);
      } else {
        setRequestDetails([]);
      }
    } catch (error) {
      console.error("Error fetching request details:", error);
      setRequestDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const fetchAllVisitsRequests = useCallback(async (days: number) => {
    if (!upn) return;
    
    setLoading(true);
    try {
      const visitsResp = await requestService.getAllVisitsRequests(Number(upn), days);
      if (visitsResp?.issuccess && visitsResp?.data) {
        const mapped = (visitsResp.data as any[]).map(mapToAllRequestRow);
        setAllVisitsRequests(mapped as AllVisitsRequestRow[]);
      } else {
        setAllVisitsRequests([]);
      }
    } catch (error) {
      console.error("Error fetching all visits requests:", error);
      setAllVisitsRequests([]);
    } finally {
      setLoading(false);
    }
  }, [upn]);

  const fetchData = useCallback(async () => {
    if (!ptId || !open) return;

    setLoading(true);
    try {
      const requestsResp = await requestService.getPatientRequests(Number(ptId));
      if (requestsResp?.issuccess && requestsResp?.data) {
        const mapped = requestsResp.data.map(mapToCompletedRequestRow);
        setCompletedRequests(mapped);
      } else {
        setCompletedRequests([]);
      }

      if (upn) {
        try {
          const pendingResp = await requestService.getPendingRequestDetails(Number(upn), PENDING_STATUS_ID);
          if (pendingResp?.issuccess && pendingResp?.data) {
            const mapped = (pendingResp.data as any[]).map((dto) => mapToRequestDetailRow(dto, false));
            setPendingRequestDetails(mapped);
          } else {
            setPendingRequestDetails([]);
          }
        } catch {
          setPendingRequestDetails([]);
        }
      }

      const outsideResp = await requestService.getPatientProcessOutsideRequests(Number(ptId));
      if (outsideResp?.issuccess && outsideResp?.data) {
        const mapped = (outsideResp.data as any[]).map((dto) => mapToRequestDetailRow(dto, true));
        setOutOfRequestServices(mapped);
      } else {
        setOutOfRequestServices([]);
      }

      const allResp = await requestService.getAllServiceRequests(Number(ptId));
      if (allResp?.issuccess && allResp?.data) {
        const mapped = (allResp.data as any[]).map(mapToAllRequestRow);
        setAllRequests(mapped);
      } else {
        setAllRequests([]);
      }

      if (upn) {
        const visitsResp = await requestService.getAllVisitsRequests(Number(upn), 1);
        if (visitsResp?.issuccess && visitsResp?.data) {
          const mapped = (visitsResp.data as any[]).map(mapToAllRequestRow);
          setAllVisitsRequests(mapped as AllVisitsRequestRow[]);
        } else {
          setAllVisitsRequests([]);
        }
      }

      try {
        const privilegeResp = await requestService.hasCancelAllReqsPrivilege();
        setHasCancelPrivilege(privilegeResp?.data ?? false);
      } catch {
        setHasCancelPrivilege(false);
      }
    } catch (error) {
      console.error("Request list fetch error:", error);
      setCompletedRequests([]);
      setRequestDetails([]);
      setPendingRequestDetails([]);
      setOutOfRequestServices([]);
      setAllRequests([]);
      setAllVisitsRequests([]);
    } finally {
      setLoading(false);
    }
  }, [ptId, upn, open]);

  useEffect(() => {
    if (selectedRequestId && typeof selectedRequestId === "number") {
      fetchRequestDetails(selectedRequestId);
    } else if (selectedRequestId) {
      fetchRequestDetails(Number(selectedRequestId));
    } else {
      setRequestDetails([]);
    }
  }, [selectedRequestId, fetchRequestDetails]);

  useEffect(() => {
    if (open && ptId) {
      fetchData();
    } else {
      setCompletedRequests([]);
      setRequestDetails([]);
      setPendingRequestDetails([]);
      setOutOfRequestServices([]);
      setAllRequests([]);
      setAllVisitsRequests([]);
      setSelectedRequestId(null);
    }
  }, [open, ptId, fetchData]);

  return {
    activeTab,
    setActiveTab: handleTabChange,
    completedRequests,
    requestDetails,
    pendingRequestDetails,
    outOfRequestServices,
    allRequests,
    allVisitsRequests,
    selectedRequestId,
    setSelectedRequestId,
    loading,
    refetch: fetchData,
    fetchRequestDetails,
    fetchAllVisitsRequests,
    hasCancelPrivilege,
    detailsLoading,
  };
}
